import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { BusinessCategory } from "src/entities/v3/PublisherResponse";
import uploadImageByFile from "src/libs/image/post-image-by-image";
import { PostImageByFileResponseSchemaV2 } from "src/libs/types/image";
import { useAuth } from "./useAuth";
import { usePut } from "./usePut";

interface Props {
  input: {
    publisherId: string | undefined;
    acceptedFiles: readonly File[];
    name: string;
    description: string;
    webUrl: string;
    phoneNumber: string;
    headquarters: string;
    founded: number;
    logoUrl: string | undefined;
    twitter: string;
    facebook: string;
    tiktok: string;
    youtube: string;
    instagram: string;
    linkedin: string;
    businessCategories: BusinessCategory[];
  };
  functions: {
    handleBackDropOpen: () => void;
    handleBackdropClose: () => void;
    refetchPublisher: () => Promise<void>;
  };
}

export const useUpdatePublisherV3 = ({ input, functions }: Props) => {
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const auth = useAuth();

  /**
   * Put publisher info (v3)
   */
  const { put } = usePut({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/publishers`,
    headers: {
      authorization: `Bearer ${auth.authInfo.accessToken}`,
    },
    onError: () => {
      enqueueSnackbar(t("hooks.edit_failed"), { variant: "error" });
    },
    onSuccess: () => {
      enqueueSnackbar(t("hooks.successfully_updated_company_information"), {
        variant: "success",
      });
    },
    onStart: () => {
      functions.handleBackDropOpen();
    },
    onEnd: async () => {
      functions.handleBackdropClose();
      await functions.refetchPublisher();
    },
  });

  const updateInfo = async () => {
    try {
      let logoUrl = input.logoUrl || "";

      // Upload new image if provided
      if (input.acceptedFiles.length > 0) {
        const data: PostImageByFileResponseSchemaV2 = await uploadImageByFile(
          input.acceptedFiles[0]
        );

        if (data.url.length === 0) {
          console.error("Image upload failed.");
          enqueueSnackbar("Image upload failed.", { variant: "error" });
          return;
        }

        logoUrl = data.url;
      }

      // Call PUT API with v3 format (camelCase)
      put({
        params: {
          name: input.name,
          phoneNumber: input.phoneNumber,
          businessCategories: input.businessCategories,
          summary: input.description,
          logoUrl: logoUrl,
          webSiteUrl: input.webUrl,
          headquarters: input.headquarters,
          founded: input.founded,
          facebookUrl: input.facebook,
          twitterUrl: input.twitter,
          linkedinUrl: input.linkedin,
          youtubeUrl: input.youtube,
          instagramUrl: input.instagram,
          tiktokUrl: input.tiktok,
        },
      });
    } catch (e) {
      enqueueSnackbar(t("hooks.update_failed"), { variant: "error" });
    }
  };

  return { updateInfo };
};
